/**
 * DuplicateOfferDialog — Duplica un'offerta dedicata intestandola a un altro
 * cliente (esistente) o a un prospect (potenziale). L'offerta duplicata
 * eredita listino, sconti, note e articoli; i prezzi base vengono
 * ricalcolati sul contesto del nuovo destinatario.
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Search, User, Sparkles, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyProspect = { business_name: "", vat_number: "", address: "", city: "", email: "", phone: "" };

export default function DuplicateOfferDialog({ open, source, onClose, onDuplicated }) {
  const [mode, setMode] = useState("customer"); // customer | prospect
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [prospectData, setProspectData] = useState(emptyProspect);
  const [language, setLanguage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("customer");
    setSelectedCustomerId("");
    setCustomerSearch("");
    setProspectData(emptyProspect);
    setLanguage(source?.language || "it");
    // fetch customers
    setLoadingCustomers(true);
    axios.get(`${API}/customers`, { params: { include_inactive: false } })
      .then((r) => setCustomers(r.data || []))
      .catch(() => setCustomers([]))
      .finally(() => setLoadingCustomers(false));
  }, [open, source]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    let base = customers;
    // Escludi il cliente sorgente (non ha senso duplicarla su se stessa)
    if (source?.customer_id) {
      base = base.filter((c) => c.id !== source.customer_id);
    }
    if (!q) return base.slice(0, 50);
    return base.filter((c) =>
      (c.business_name || "").toLowerCase().includes(q) ||
      (c.codice_cliente || "").toLowerCase().includes(q) ||
      (c.vat_number || "").toLowerCase().includes(q)
    ).slice(0, 100);
  }, [customers, customerSearch, source]);

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  const canSubmit = () => {
    if (submitting) return false;
    if (mode === "customer") return !!selectedCustomerId;
    return !!(prospectData.business_name || "").trim();
  };

  const handleSubmit = async () => {
    if (!source || !canSubmit()) return;
    setSubmitting(true);
    try {
      const body = {
        language: language || undefined,
        is_prospect: mode === "prospect",
      };
      if (mode === "prospect") {
        body.prospect_data = { ...prospectData, business_name: prospectData.business_name.trim() };
      } else {
        body.customer_id = selectedCustomerId;
      }
      const r = await axios.post(`${API}/dedicated-offers/${source.id}/duplicate`, body);
      toast.success(`Offerta duplicata per ${r.data.customer_name}`);
      onDuplicated?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la duplicazione");
    } finally {
      setSubmitting(false);
    }
  };

  if (!source) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="duplicate-offer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Copy className="w-5 h-5 text-[#8A1C2A]" />
            Duplica offerta dedicata
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Duplico l&apos;offerta di <b>{source.customer_name}</b> ({source.pricelist_type_name}, {(source.items || []).length} articoli) e la intesto a un altro cliente o prospect. Sconti e note vengono mantenuti; i prezzi base sono ricalcolati sul contesto del nuovo destinatario.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-3">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode("customer")}
              className={`p-3 rounded-lg border-2 transition text-left ${mode === "customer" ? "border-[#8A1C2A] bg-red-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
              data-testid="dup-mode-customer-btn"
            >
              <div className="flex items-center gap-2 mb-1">
                <User className={`w-4 h-4 ${mode === "customer" ? "text-[#8A1C2A]" : "text-stone-500"}`} />
                <span className={`font-semibold text-sm ${mode === "customer" ? "text-[#8A1C2A]" : "text-stone-700"}`}>Cliente esistente</span>
              </div>
              <p className="text-[11px] text-stone-500">Scegli un cliente già in anagrafica</p>
            </button>
            <button
              type="button"
              onClick={() => setMode("prospect")}
              className={`p-3 rounded-lg border-2 transition text-left ${mode === "prospect" ? "border-[#C5A059] bg-amber-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
              data-testid="dup-mode-prospect-btn"
            >
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className={`w-4 h-4 ${mode === "prospect" ? "text-[#C5A059]" : "text-stone-500"}`} />
                <span className={`font-semibold text-sm ${mode === "prospect" ? "text-[#C5A059]" : "text-stone-700"}`}>Cliente potenziale</span>
              </div>
              <p className="text-[11px] text-stone-500">Prospect: dati inseriti a mano, non salvato in anagrafica</p>
            </button>
          </div>

          {/* Customer picker */}
          {mode === "customer" && (
            <div className="space-y-2">
              <Label>Seleziona il nuovo cliente</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  className="pl-10"
                  placeholder="Cerca per nome, codice, P.IVA..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  data-testid="dup-customer-search"
                />
              </div>
              <div className="border border-stone-200 rounded-lg max-h-72 overflow-y-auto divide-y divide-stone-100 bg-white">
                {loadingCustomers && (
                  <div className="py-6 text-center text-xs text-stone-400">Caricamento clienti...</div>
                )}
                {!loadingCustomers && filteredCustomers.length === 0 && (
                  <div className="py-6 text-center text-xs text-stone-400">Nessun cliente trovato</div>
                )}
                {filteredCustomers.map((c) => {
                  const isSel = c.id === selectedCustomerId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCustomerId(c.id)}
                      className={`w-full text-left px-3 py-2 hover:bg-stone-50 transition ${isSel ? "bg-red-50" : ""}`}
                      data-testid={`dup-customer-${c.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${isSel ? "text-[#8A1C2A]" : "text-stone-900"}`}>
                            {c.business_name}
                          </p>
                          <p className="text-[11px] text-stone-500">
                            {c.codice_cliente || "—"} · {c.vat_number || "—"} · {c.city || "—"}
                          </p>
                        </div>
                        {isSel && (
                          <span className="text-[10px] font-semibold text-[#8A1C2A] bg-white border border-[#8A1C2A] rounded px-2 py-0.5">
                            SELEZIONATO
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedCustomer && (
                <p className="text-xs text-emerald-700">
                  ✓ L&apos;offerta sarà intestata a <b>{selectedCustomer.business_name}</b>
                </p>
              )}
            </div>
          )}

          {/* Prospect form */}
          {mode === "prospect" && (
            <div className="space-y-3 bg-amber-50/40 border border-amber-200 rounded-lg p-3">
              <div>
                <Label htmlFor="dup-prospect-name">Ragione sociale / Nome *</Label>
                <Input
                  id="dup-prospect-name"
                  value={prospectData.business_name}
                  onChange={(e) => setProspectData((p) => ({ ...p, business_name: e.target.value }))}
                  placeholder="Es. Rossi S.r.l."
                  data-testid="dup-prospect-name-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="dup-prospect-vat">P.IVA</Label>
                  <Input
                    id="dup-prospect-vat"
                    value={prospectData.vat_number}
                    onChange={(e) => setProspectData((p) => ({ ...p, vat_number: e.target.value }))}
                    data-testid="dup-prospect-vat-input"
                  />
                </div>
                <div>
                  <Label htmlFor="dup-prospect-city">Città</Label>
                  <Input
                    id="dup-prospect-city"
                    value={prospectData.city}
                    onChange={(e) => setProspectData((p) => ({ ...p, city: e.target.value }))}
                    data-testid="dup-prospect-city-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="dup-prospect-address">Indirizzo</Label>
                  <Input
                    id="dup-prospect-address"
                    value={prospectData.address}
                    onChange={(e) => setProspectData((p) => ({ ...p, address: e.target.value }))}
                    data-testid="dup-prospect-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="dup-prospect-email">Email</Label>
                  <Input
                    id="dup-prospect-email"
                    type="email"
                    value={prospectData.email}
                    onChange={(e) => setProspectData((p) => ({ ...p, email: e.target.value }))}
                    data-testid="dup-prospect-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="dup-prospect-phone">Telefono</Label>
                  <Input
                    id="dup-prospect-phone"
                    value={prospectData.phone}
                    onChange={(e) => setProspectData((p) => ({ ...p, phone: e.target.value }))}
                    data-testid="dup-prospect-phone-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Language selector */}
          <div>
            <Label>Lingua PDF</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-full" data-testid="dup-language-select">
                <SelectValue placeholder="Scegli lingua PDF" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="it">🇮🇹 Italiano</SelectItem>
                <SelectItem value="en">🇬🇧 English</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-stone-500 mt-1">
              La sorgente era in <b>{(source.language || "it").toUpperCase()}</b>. Puoi cambiare la lingua della copia se serve.
            </p>
          </div>
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            <X className="w-4 h-4 mr-1" />Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit()}
            className="bg-[#8A1C2A] hover:bg-[#6e1622]"
            data-testid="dup-submit-btn"
          >
            <Copy className="w-4 h-4 mr-1" />
            {submitting ? "Duplico..." : "Duplica offerta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
